import { CSSResultGroup, TemplateResult, html } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';

import OutlineDropdown from '../../base/outline-dropdown/outline-dropdown';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './se-dropdown.css.lit';
import { SeButtonVariant } from '../se-button/se-button';
import '../se-split-button/se-split-button';
import '../se-button/se-button';
import { SeMainHeader } from '../../se-design/se-main-header/se-main-header';
import { MobileController } from '../../controllers/mobile-controller';
/**
 * The SeDropdown component
 * @element se-dropdown
 */
@customElement('se-dropdown')
export class SeDropdown extends OutlineDropdown {
  static styles: CSSResultGroup = [componentStyles];
  mobileController = new MobileController(this);

  /**
   * The button style variant to use.
   */
  @property({ type: String, attribute: 'trigger-variant', reflect: true })
  seTriggerVariant: SeButtonVariant = 'dropdown-nav';

  /**
   * @todo: Ensure there IS a dropdown before triggering the functionality.
   * @returns {TemplateResult}
   */

  render(): TemplateResult {
    return html`
      <div
        id="dropdown-${this.seed}"
        class=${classMap({
          'dropdown': true,
          'dropdown--open': this.isOpen,
        })}
      >
        <div
          class="dropdown__trigger"
          @mouseenter="${!this.mobileController.isMobile ? this.show : null}"
          @mouseleave="${this.mobileController.isMobile
            ? null
            : this.handleFocusChange}"
        >
          ${this.buttonTemplate()} ${this.dropdownTemplate()}
        </div>
      </div>
    `;
  }

  /**
   * Template partial for the dropdown rendering.
   * @returns TemplateResult | null
   */
  dropdownTemplate(): TemplateResult | null {
    if (!this.hasDropdown) return null;

    return html`
      <div class="dropdown__panel" @keydown="${this.handlePanelKeystrokes}">
        ${this.headerTemplate()}
        <div class="control-spacer">
          <slot name="dropdown"></slot>
        </div>
        ${this.footerTemplate()}
      </div>
    `;
  }

  /**
   * Template partial for the "button" rendering.
   * @returns TemplateResult | null
   */
  buttonTemplate(): TemplateResult | null {
    return this.triggerUrl
      ? html`
          <se-split-button
            variant="${ifDefined(this.seTriggerVariant)}"
            button-target="${ifDefined(this.triggerTarget)}"
            button-url="${ifDefined(this.triggerUrl)}"
            button-label="${ifDefined(this.triggerLabel)}"
            @mouseenter="${this.handleHoverStyles}"
            @keydown="${this.handleButtonTrigger}"
            ?is-disabled=${this.isDisabled}
            controls="panel-${this.seed}"
            expanded="${this.isOpen ? 'true' : 'false'}"
          >
            <span
              class=${classMap({
                button__trigger: true,
                button__has_menu:
                  this.hasDropdown && this.seTriggerVariant == 'primary',
              })}
              >${this.triggerText}</span
            >
            ${this.iconWrapperTemplate()}
          </se-split-button>
        `
      : html`
          <se-button
            variant="${ifDefined(this.seTriggerVariant)}"
            button-target="${ifDefined(this.triggerTarget)}"
            button-url="${ifDefined(this.triggerUrl)}"
            button-label="${ifDefined(this.triggerLabel)}"
            @keydown="${this.handleButtonTrigger}"
            @click="${this.handleIconClick}"
            ?is-disabled=${this.isDisabled}
            controls="panel-${this.seed}"
            expanded="${this.isOpen ? 'true' : 'false'}"
          >
            <span class="button__trigger">${this.triggerText}</span>
            ${this.iconWrapperTemplate()}
          </se-button>
        `;
  }

  /**
   * Template partial for the icon rendering.
   * @todo: Add SR text for button.
   * @returns TemplateResult | null
   */
  iconWrapperTemplate(): TemplateResult | null {
    if (!this.hasDropdown) return null;

    return html`
      <span
        class=${classMap({
          icon__trigger: true,
          icon__bordered:
            this.triggerUrl && this.seTriggerVariant !== 'primary',
        })}
      >
        ${this.triggerUrl
          ? html` <se-button
              class="icon__button"
              variant="${ifDefined(this.seTriggerVariant)}"
              button-target="${ifDefined(this.triggerTarget)}"
              button-label="${ifDefined(this.triggerLabel)}"
              @keydown="${this.handleIconTrigger}"
              @click="${this.handleIconClick}"
              ?is-disabled=${this.isDisabled}
            >
              ${this.iconTemplate()}
              <span class="sr-only"
                >Sub-navigation for ${this.triggerText}</span
              >
            </se-button>`
          : html` ${this.iconTemplate()} `}
      </span>
    `;
  }

  /**
   * Template partial for the icon rendering.
   * @todo: Ensure chevron can be swapped out for other icons.
   * @todo: Ensure "standard" chevron-down can be replaced/rotated when menu is open.
   * @returns TemplateResult | null
   */
  iconTemplate(): TemplateResult | null {
    if (this.isOpen) {
      return html`
        <outline-icon
          slot="right"
          name="${'chevron-up'}"
          library="system"
          size="1em"
          color="brand-primary-2"
          label="${ifDefined(this.triggerUrl ? this.triggerLabel : undefined)}"
        ></outline-icon>
      `;
    } else {
      return html`
        <outline-icon
          slot="right"
          name="${'chevron-down'}"
          library="system"
          size="1em"
          color="brand-primary-2"
          label="${ifDefined(this.triggerUrl ? this.triggerLabel : undefined)}"
        ></outline-icon>
      `;
    }
  }

  handleIconClick(event: MouseEvent) {
    const header = document.querySelector('se-main-header') as SeMainHeader;
    event.preventDefault();

    if (this.seTriggerVariant !== 'dropdown-nav') {
      return this.isOpen ? this.hide() : this.show();
    }

    if (header.searchOpen) {
      header.closeSearchPanel();
    }

    if (this.isOpen) {
      this.hide();
      return header.removeOverlay();
    } else {
      this.show();
      return header.applyOverlay();
    }
  }
}
